import React, { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import AllocatorProfileItem from './AllocatorProfileIndexItem/allocatorProfileIndexItem';
import AllocatorProfileCardRecommendationContainer from 'buyside/components/container/AllocatorProfileIndex/AllocatorProfileCardRecommendationContainer';
import AllocatorProfileFilters from 'buyside/components/presentation/AllocatorProfileIndexPage/allocatorProfileFilters';
import Button, { BUTTON_SIZES, BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
import IndexPageHeader from 'buyside/components/presentation/IndexPageHeader/indexPageHeader';
import IndexPageListArea from 'buyside/components/presentation/IndexPageListArea/indexPageListArea';
import IndexPaginationNav from 'buyside/components/presentation/IndexPaginationNav/indexPaginationNav';

import { BREAKPOINTS, useIsMobile } from 'buyside/util/hooks/useIsMobile';
import { validatePageParam } from 'buyside/util/numbers';
import { inviteMembersUrl } from 'buyside/util/urlHelper';
const { indexTypes } = require('buyside/util/constantsFromRails.json');

import styles from './allocatorProfileIndexPageStyles.scss';

import {
  useAllocatorProfileIndexListQuery,
  useAllocatorProfileCurrentUserQuery,
  useAllocatorProfileRecommendationsQuery
} from 'interfaces/graphql-typings';

const AllocatorProfileIndexPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const isMobile = useIsMobile(BREAKPOINTS.LARGE);

  const page = validatePageParam(searchParams);
  const maybeFundProfileId = searchParams.get('fpid');
  const showRecommendationsOnly = searchParams.get('recommended') === 'true';
  const indexType = indexTypes.allocator;

  const {
    data,
    loading: allocatorProfilesLoading,
    error: allocatorProfilesError,
    previousData
  } = useAllocatorProfileIndexListQuery({
    variables: { indexType, searchParams: searchParams.toString() },
    skip: showRecommendationsOnly
  });

  if (allocatorProfilesError) throw allocatorProfilesError;

  const {
    data: userData,
    previousData: prevUserData,
    loading: userLoading,
    error: allocatorProfileCurrentUserError
  } = useAllocatorProfileCurrentUserQuery();

  if (allocatorProfileCurrentUserError) throw allocatorProfileCurrentUserError;

  const {
    data: recommendationsData,
    loading: recommendationsLoading,
    error: recommendationsError
  } = useAllocatorProfileRecommendationsQuery({
    variables: {
      showAll: showRecommendationsOnly,
      fundProfileId: maybeFundProfileId
    },
    skip:
      Boolean(
        Array.from(searchParams.keys()).filter(
          key => !['page', 'recommended', 'fpid'].includes(key)
        ).length
      ) || !(page === 1 || showRecommendationsOnly)
  });

  if (recommendationsError) throw recommendationsError;

  const allocatorProfilesIndexList = (data || previousData)?.allocatorProfilesIndexList;
  const allocatorProfilesIndexListResultCount =
    (data || previousData)?.allocatorProfilesIndexListResultCount || 0;
  const currentUser = (userData || prevUserData)?.currentUser;
  const allocatorRecommendations = recommendationsData?.allocatorRecommendations;
  const recommendedAllocatorsCount = allocatorRecommendations?.edges?.length || 0;

  const renderFilters = useCallback(
    () => (
      <AllocatorProfileFilters
        adminFundProfiles={currentUser?.adminFundProfiles || []}
        selectedFundId={maybeFundProfileId}
        setSearchParams={setSearchParams}
      />
    ),
    [currentUser?.adminFundProfiles, maybeFundProfileId, setSearchParams]
  );
  const renderSideButtons = useCallback(
    () => (
      <Button
        type={BUTTON_TYPES.PRIMARY}
        size={BUTTON_SIZES.SMALL}
        text="Invite New Members"
        url={inviteMembersUrl()}
      />
    ),
    []
  );

  return (
    <main className={styles.main}>
      <div className={styles.indexPageContainer}>
        <div className={styles.mainIndexPageArea}>
          <IndexPageHeader
            header={showRecommendationsOnly ? 'Recommended Allocators' : 'Allocators'}
            renderFilters={renderFilters}
            renderSideButtons={renderSideButtons}
            resultCount={
              showRecommendationsOnly
                ? recommendedAllocatorsCount
                : allocatorProfilesIndexListResultCount
            }
          />
          <IndexPageListArea
            data={allocatorProfilesIndexList}
            loading={allocatorProfilesLoading || recommendationsLoading || userLoading}
            currentUser={currentUser}
            card={AllocatorProfileItem}
            recommendationComponent={AllocatorProfileCardRecommendationContainer}
            recommendations={allocatorRecommendations}
            showOnlyRecommended={showRecommendationsOnly}
            isMobile={isMobile}
          />
          {showRecommendationsOnly ? null : (
            <IndexPaginationNav
              resultCount={allocatorProfilesIndexListResultCount || 0}
              loading={allocatorProfilesLoading || recommendationsLoading}
              resultLength={allocatorProfilesIndexList?.length || 0}
              resultsPerPage={20}
            />
          )}
        </div>
      </div>
    </main>
  );
};

export default AllocatorProfileIndexPage;
